import {
  GetJobProgressionRequest,
  SetAuthorizationRequestedProgressionRequest,
  SetAuthorizationSignedProgressionRequest,
  SetDraftApprovedProgressionRequest,
  SetDraftInReviewProgressionRequest,
  SetDraftRejectedProgressionRequest,
  OverrideJobProgressionStepRequest,
  SetJobStartedProgressionRequest,
  SetOpenItemsProgressionRequest,
  SetReturnFiledProgressionRequest,
  OverrideableJobProgressionStepStatus as OverrideableJobProgressionStepStatusProto,
} from '@taxfyle/api-internal/internal/job_progression_pb'
import { collection } from 'infra/collection'
import { action } from 'mobx'
import { Id } from 'types/definitions'
import { JobProgressionAPI } from './API'
import { JobProgressionDto } from './dtos'
import {
  JobProgression,
  OverrideableJobProgressionStepStatus,
} from './JobProgression'
import { match } from 'utils/pattern-match-util'

/**
 * Progression prime delegate
 */
export type PrimeProgression = (primeProps: {
  jobId?: string
}) => JobProgression

export default class JobProgressionStore {
  constructor(private api: JobProgressionAPI) {}

  progressions = collection<JobProgressionDto, JobProgression>({
    create: () => new JobProgression(),
    getModelId: (p) => p.jobId,
    getDataId: (p) => p.jobId,
  })

  /**
   * Prime singular job progression step.
   *
   * @param props
   */
  readonly prime: PrimeProgression = ({ jobId }) => {
    const existing = this.progressions.get(jobId)
    if (existing) {
      return existing
    }
    return this.progressions.set({ jobId })
  }

  /**
   * Looks up progression steps by job ID.
   */
  progressionForJob = (jobId?: string) =>
    this.progressions.find((p) => p.jobId === jobId)

  /**
   * Lists all progression steps.
   * @param jobId
   * @returns
   */
  async fetchProgression(jobId: Id) {
    const request = new GetJobProgressionRequest()
    request.setJobId(Id.assert(jobId))

    const response = await this.api.getProgression(request)
    const obj = response.toObject()

    this.progressions.set({
      id: obj.id,
      jobId,
      flow: obj.progressionFlow,
      steps: obj.progressionStepsList,
    })
  }

  /**
   * Sets the current progression to jobp started.
   * @param jobId
   */
  async setOpenItemsProgression(jobId: Id) {
    const request = new SetOpenItemsProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setOpenItemsProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to job started.
   * @param jobId
   */
  @action.bound
  async setJobStartedProgression(jobId: Id) {
    const request = new SetJobStartedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setJobStartedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to draft in review.
   * @param jobId
   */
  async setDraftInReviewProgression(jobId: Id) {
    const request = new SetDraftInReviewProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setDraftInReviewProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to draft approved
   */
  async setDraftApprovedProgression(jobId: Id) {
    const request = new SetDraftApprovedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setDraftApprovedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to draft rejected
   */
  async setDraftRejectedProgression(jobId: Id) {
    const request = new SetDraftRejectedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setDraftRejectedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to authorization requested
   */
  async setAuthorizationRequestedProgression(jobId: Id) {
    const request = new SetAuthorizationRequestedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setAuthorizationRequestedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to authorization signed
   */
  async setAuthorizationSignedProgression(jobId: Id) {
    const request = new SetAuthorizationSignedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setAuthorizationSignedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to return filed
   */
  async setReturnFiledProgression(jobId: Id) {
    const request = new SetReturnFiledProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setReturnFiledProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to specific step
   */
  async overrideProgressionStep(
    jobId: Id,
    stepId: OverrideableJobProgressionStepStatus
  ) {
    const request = new OverrideJobProgressionStepRequest()
      .setJobId(Id.assert(jobId))
      .setProgressionStep(this.toOverrideableProgressionStepDto(stepId))

    await this.api.overrideJobProgressionStep(request)
    await this.fetchProgression(jobId)
  }

  toOverrideableProgressionStepDto(
    stepId: OverrideableJobProgressionStepStatus
  ) {
    return match(stepId, {
      [OverrideableJobProgressionStepStatus.RETURN_FILED]: () =>
        OverrideableJobProgressionStepStatusProto.ADMIN_RETURN_FILED,
    })
  }
}
